import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CardV2 as Card, EmptyState } from '@feathr/components';

import type { IAdGroup } from '../../AdStep/AdStep.types';
import AdGroupReviewCard from './AdGroupReviewCard';

interface IAdGroupsReviewProps {
  readonly adGroups: IAdGroup[];
  /** Jumps to the Ads step and opens the given ad group for editing there. */
  readonly onEdit: (adGroupKey: string) => void;
  /** The advertiser name Google shows above each ad's display URL. */
  readonly businessName?: string;
}

/**
 * The ad group summary a Search campaign shows on the Review step, standing in for the
 * separate Keyword Themes, Negative Keywords and Ad Text cards a Smart campaign shows —
 * Search has no keyword themes, and its ad text belongs to an ad group rather than the
 * campaign.
 *
 * @param props - {@link IAdGroupsReviewProps}.
 */
function AdGroupsReview({
  adGroups,
  onEdit,
  businessName,
}: Readonly<IAdGroupsReviewProps>): JSX.Element {
  const { t } = useTranslation();

  if (!adGroups.length) {
    return (
      <Card name={'review-ad-groups-empty'}>
        <Card.Header title={t('Ad groups')} />
        <Card.Content>
          {/*
           * No action here: the Edit buttons on this step belong to an ad group, and there
           * is none to edit. The description says where to go instead.
           */}
          <EmptyState
            description={t('Add them on the Ads step before publishing.')}
            label={t('No ad groups yet')}
            name={'review-ad-groups-empty-state'}
            theme={'neutral'}
            width={'full'}
          />
        </Card.Content>
      </Card>
    );
  }

  return (
    <>
      {adGroups.map((adGroup, index) => (
        <AdGroupReviewCard
          adGroup={adGroup}
          businessName={businessName}
          index={index}
          key={adGroup.key}
          onEdit={onEdit}
        />
      ))}
    </>
  );
}

export default observer(AdGroupsReview);
